import { createContext, useContext } from 'react';

export type Lang = 'ar' | 'en';

/**
 * Small hand-rolled dictionary rather than an i18n library: the surface is one
 * dashboard, and this keeps the bundle and the mental overhead small.
 */
const STRINGS = {
  // auth
  signIn: { ar: 'تسجيل الدخول', en: 'Sign in' },
  tagline: { ar: 'تتبّع مركباتك، لحظة بلحظة', en: 'Your vehicles, moment by moment' },
  email: { ar: 'البريد الإلكتروني', en: 'Email' },
  password: { ar: 'كلمة المرور', en: 'Password' },
  signOut: { ar: 'خروج', en: 'Sign out' },
  invalidLogin: { ar: 'البريد أو كلمة المرور غير صحيحة', en: 'Invalid email or password' },
  serverUnreachable: {
    ar: 'مفيش اتصال بالسيرفر. اتأكد إنه شغال وحاول تاني.',
    en: 'Cannot reach the server. Check that it is running and try again.',
  },

  // nav
  live: { ar: 'مباشر', en: 'Live' },
  replay: { ar: 'الرحلات', en: 'Replay' },
  admin: { ar: 'الإدارة', en: 'Admin' },

  // device list
  vehicles: { ar: 'المركبات', en: 'Vehicles' },
  search: { ar: 'بحث', en: 'Search' },
  online: { ar: 'متصل', en: 'Online' },
  offline: { ar: 'غير متصل', en: 'Offline' },
  noVehicles: { ar: 'لا توجد مركبات', en: 'No vehicles yet' },
  noPosition: { ar: 'لا يوجد موقع بعد', en: 'No position yet' },

  // status
  ignition: { ar: 'الكونتاكت', en: 'Ignition' },
  on: { ar: 'مفتوح', en: 'On' },
  off: { ar: 'مقفول', en: 'Off' },
  engine: { ar: 'الموتور', en: 'Engine' },
  running: { ar: 'يعمل', en: 'Running' },
  cut: { ar: 'مقطوع', en: 'Cut off' },
  alarm: { ar: 'الإنذار', en: 'Alarm' },
  armed: { ar: 'مسلّح', en: 'Armed' },
  disarmed: { ar: 'غير مسلّح', en: 'Disarmed' },
  battery: { ar: 'البطارية', en: 'Battery' },
  signal: { ar: 'الشبكة', en: 'Signal' },
  speed: { ar: 'السرعة', en: 'Speed' },
  lastUpdate: { ar: 'آخر تحديث', en: 'Last update' },
  unknown: { ar: 'غير معروف', en: 'Unknown' },

  // actions
  openInGoogleMaps: { ar: 'افتح في خرائط جوجل', en: 'Open in Google Maps' },
  myLocation: { ar: 'موقعي', en: 'My location' },
  distanceToCar: { ar: 'المسافة للعربية', en: 'Distance to vehicle' },
  directions: { ar: 'الاتجاهات', en: 'Directions' },
  stopEngine: { ar: 'إطفاء الموتور', en: 'Cut engine' },
  resumeEngine: { ar: 'تشغيل الموتور', en: 'Restore engine' },
  arm: { ar: 'تسليح', en: 'Arm' },
  disarm: { ar: 'إلغاء التسليح', en: 'Disarm' },
  // Named for what it does to the tracker, not for what the map shows. "Locate"
  // read as "centre the map on it", which is not what it costs a command to do.
  // The APK is served by Caddy from the same manifest the app checks on launch,
  // so the version offered here can never differ from the one an installed app
  // is being told to update to.
  downloadApp: { ar: 'حمّل تطبيق أندرويد', en: 'Download the Android app' },
  // Deliberately not "parked". The tracker stopped talking, which may be an
  // underground car park, a cut aerial, or a flat battery — and the platform
  // cannot tell those apart.
  vehicleOffline: {
    ar: '{name} بطّلت ترسل',
    en: '{name} stopped reporting',
  },
  notifyThisVehicle: {
    ar: 'إشعارات المركبة دي',
    en: 'Alerts for this vehicle',
  },
  forwardTitle: { ar: 'إرسال نسخة لسيرفر تاني', en: 'Forward to a second server' },
  forwardHint: {
    ar: 'كل رسالة من الجهاز هتتبعت زي ما هي لسيرفر تاني كمان. بيبدأ من أول اتصال جديد للجهاز.',
    en: "Every message from this tracker is copied to another server, unchanged. Starts on the tracker's next connection.",
  },
  forwardHost: { ar: 'العنوان أو الـIP', en: 'Host or IP' },
  forwardPort: { ar: 'البورت', en: 'Port' },
  forwardSaved: {
    ar: 'اتحفظ. هيبدأ يبعت من أول اتصال جديد للجهاز.',
    en: "Saved. It starts on the tracker's next connection.",
  },
  forwardOff: { ar: 'اتوقف الإرسال', en: 'Forwarding stopped' },
  forwardStop: { ar: 'إيقاف', en: 'Stop' },
  backup: { ar: 'تنزيل نسخة احتياطية', en: 'Download a backup' },
  backupHint: {
    ar: 'كل الحسابات والمركبات والصلاحيات وتاريخ المواقع، ملف واحد. مفيش باسوردات جواه.',
    en: 'Every account, vehicle, grant and position, in one file. No passwords in it.',
  },
  backupStarted: {
    ar: 'التنزيل بدأ — ممكن ياخد شوية لو التاريخ كبير',
    en: 'Download started — a long history takes a moment',
  },
  locate: { ar: 'حدّث موقع الجهاز الآن', en: 'Refresh device position' },
  locateHint: {
    ar: 'بيطلب من الجهاز يبعت موقعه فوراً بدل ما تستنى الإرسال التالي',
    en: 'Asks the tracker to report right now instead of waiting for its next fix',
  },
  locateSent: {
    ar: 'الطلب اتبعت — الموقع هيتحدّث خلال ثواني',
    en: 'Requested — the position should update within seconds',
  },
  engineIsCut: {
    ar: 'الموتور مقطوع حالياً',
    en: 'The engine is currently cut off',
  },
  engineStateUnknown: {
    ar: 'حالة الموتور غير معروفة — الجهاز لم يبلّغ بها بعد.',
    en: 'Engine state unknown — the device has not reported it yet.',
  },
  sending: { ar: 'جارٍ الإرسال...', en: 'Sending...' },
  queuedOffline: {
    ar: 'الجهاز غير متصل — الأمر في الانتظار',
    en: 'Device is offline, command queued',
  },
  commandSent: { ar: 'تم إرسال الأمر', en: 'Command sent' },

  // confirm
  confirmCutTitle: { ar: 'تأكيد إطفاء الموتور', en: 'Confirm engine cut' },
  confirmCutBody: {
    ar: 'سيتم قطع الوقود عن المركبة. لا تفعل ذلك إلا والمركبة واقفة.',
    en: 'This cuts fuel to the vehicle. Only do this when it is stopped.',
  },
  confirm: { ar: 'تأكيد', en: 'Confirm' },
  cancel: { ar: 'إلغاء', en: 'Cancel' },

  // replay
  from: { ar: 'من', en: 'From' },
  to: { ar: 'إلى', en: 'To' },
  today: { ar: 'اليوم', en: 'Today' },
  yesterday: { ar: 'أمس', en: 'Yesterday' },
  last7: { ar: 'آخر ٧ أيام', en: 'Last 7 days' },
  last30: { ar: 'آخر ٣٠ يوم', en: 'Last 30 days' },
  load: { ar: 'عرض', en: 'Load' },
  trips: { ar: 'الرحلات', en: 'Trips' },
  // A vehicle can report all day without driving anywhere. Saying "no trips"
  // alone reads as "no data", which is a different and much more alarming
  // thing for a customer to conclude.
  noTripsButParked: {
    ar: 'المركبة ما تحركتش في الفترة دي. النقط المسجّلة معروضة على الخريطة',
    en: 'The vehicle did not move in this period. Its recorded fixes are on the map',
  },
  noTrips: { ar: 'لا توجد رحلات في هذه الفترة', en: 'No trips in this period' },
  distance: { ar: 'المسافة', en: 'Distance' },
  duration: { ar: 'المدة', en: 'Duration' },
  maxSpeed: { ar: 'أقصى سرعة', en: 'Max speed' },
  avgSpeed: { ar: 'متوسط السرعة', en: 'Avg speed' },
  totalDistance: { ar: 'إجمالي المسافة', en: 'Total distance' },
  movingTime: { ar: 'وقت الحركة', en: 'Moving time' },
  idleTime: { ar: 'وقت التوقف', en: 'Idle time' },
  play: { ar: 'تشغيل', en: 'Play' },
  pause: { ar: 'إيقاف', en: 'Pause' },
  playbackSpeed: { ar: 'سرعة العرض', en: 'Playback speed' },
  course: { ar: 'الاتجاه', en: 'Heading' },

  // admin
  devices: { ar: 'الأجهزة', en: 'Devices' },
  users: { ar: 'المستخدمون', en: 'Users' },
  addDevice: { ar: 'إضافة جهاز', en: 'Add device' },
  addUser: { ar: 'إضافة مستخدم', en: 'Add user' },
  imei: { ar: 'IMEI', en: 'IMEI' },
  name: { ar: 'الاسم', en: 'Name' },
  model: { ar: 'الموديل', en: 'Model' },
  plate: { ar: 'رقم اللوحة', en: 'Plate' },
  simNumber: { ar: 'رقم الشريحة', en: 'SIM number' },
  hasImmobiliser: { ar: 'به ريلاي قطع', en: 'Has cut-off relay' },
  phone: { ar: 'الهاتف', en: 'Phone' },
  role: { ar: 'الصلاحية', en: 'Role' },
  save: { ar: 'حفظ', en: 'Save' },
  delete: { ar: 'حذف', en: 'Delete' },
  assignedVehicles: { ar: 'المركبات المخصصة', en: 'Assigned vehicles' },
  manageAccess: { ar: 'إدارة الصلاحيات', en: 'Manage access' },
  confirmDelete: { ar: 'تأكيد الحذف؟', en: 'Delete for good?' },

  // map + filters
  follow: { ar: 'تتبّع', en: 'Follow' },
  mapClean: { ar: 'فاتح', en: 'Light' },
  mapStreets: { ar: 'شوارع', en: 'Streets' },
  mapNight: { ar: 'ليلي', en: 'Night' },
  filterAll: { ar: 'الكل', en: 'All' },
  filterMoving: { ar: 'تتحرك', en: 'Moving' },
  filterParked: { ar: 'واقفة', en: 'Parked' },
  filterOffline: { ar: 'غير متصلة', en: 'Offline' },
  noMatch: { ar: 'لا توجد نتائج', en: 'No matches' },
  fitAll: { ar: 'عرض الكل', en: 'Fit all' },

  // feedback
  deviceCreated: { ar: 'تمت إضافة الجهاز', en: 'Device added' },
  deviceDeleted: { ar: 'تم حذف الجهاز', en: 'Device deleted' },
  userCreated: { ar: 'تم إنشاء الحساب', en: 'Account created' },
  accessSaved: { ar: 'تم حفظ الصلاحيات', en: 'Access saved' },
  accessNone: { ar: 'لا توجد مركبات مخصصة', en: 'No vehicles assigned' },
  selectAll: { ar: 'تحديد الكل', en: 'Select all' },
  clearAll: { ar: 'إلغاء الكل', en: 'Clear all' },
  currentlyAssigned: { ar: 'مخصص حالياً', en: 'Currently assigned' },
  noDevicesYet: { ar: 'أضف جهازاً أولاً', en: 'Add a device first' },

  // movement alerts
  notifications: { ar: 'التنبيهات', en: 'Notifications' },
  notificationsOn: { ar: 'التنبيهات مفعّلة', en: 'Notifications on' },
  notificationsOff: { ar: 'التنبيهات مقفولة', en: 'Notifications off' },
  vehicleStarted: { ar: '{name} بدأت التحرك', en: '{name} started moving' },
  vehicleStopped: { ar: '{name} وقفت', en: '{name} has stopped' },
  allowDesktopAlerts: {
    ar: 'اسمح بالتنبيهات على سطح المكتب عشان توصلك والصفحة مقفولة',
    en: 'Allow desktop alerts so they reach you when the tab is in the background',
  },
  desktopAlertsBlocked: {
    ar: 'التنبيهات محظورة من المتصفح — التنبيهات هتظهر داخل الصفحة بس',
    en: 'The browser is blocking alerts — they will only show inside the page',
  },

  // theme
  lightMode: { ar: 'الوضع الفاتح', en: 'Light mode' },
  darkMode: { ar: 'الوضع الليلي', en: 'Dark mode' },

  // login extras
  showPassword: { ar: 'إظهار كلمة المرور', en: 'Show password' },
  hidePassword: { ar: 'إخفاء كلمة المرور', en: 'Hide password' },
  forgotPassword: { ar: 'نسيت كلمة المرور؟', en: 'Forgot your password?' },
  forgotBody: {
    ar: 'لا يمكن إعادة تعيين كلمة المرور من هنا. ابعت رسالة على البريد التالي وهيتم ضبطها لك.',
    en: 'Passwords cannot be reset from here. Email the address below and it will be reset for you.',
  },
  forgotNoAddress: {
    ar: 'لم يتم ضبط بريد للدعم. تواصل مع مدير النظام.',
    en: 'No support address is configured. Contact your administrator.',
  },

  // replay extras
  mapSatellite: { ar: 'قمر صناعي', en: 'Satellite' },
  wholePeriod: { ar: 'عرض الفترة كلها', en: 'Play whole period' },
  showingFixes: {
    ar: 'يعرض {shown} نقطة من أصل {total}',
    en: 'showing {shown} of {total} fixes',
  },

  // registration and claiming
  createAccount: { ar: 'إنشاء حساب', en: 'Create account' },
  noAccount: { ar: 'ليس لديك حساب؟ سجّل الآن', en: 'No account? Register' },
  haveAccount: { ar: 'لديك حساب؟ سجّل الدخول', en: 'Have an account? Sign in' },
  passwordHint: { ar: '٨ حروف على الأقل', en: 'At least 8 characters' },
  addMyVehicle: { ar: 'أضف عربيتي', en: 'Add my vehicle' },
  addVehicle: { ar: 'إضافة', en: 'Add' },
  claimHelp: {
    ar: 'اكتب بيانات عربيتك. الطلب هيتراجع من الإدارة قبل ما تتضاف لحسابك.',
    en: 'Fill in your vehicle details. An administrator reviews the request before it is added to your account.',
  },
  vehicleName: { ar: 'اسم العربية', en: 'Vehicle name' },
  vehicleNamePlaceholder: { ar: 'عربية البيت', en: 'Family car' },
  proposedDetails: { ar: 'بيانات العربية من العميل', en: 'Vehicle details from the customer' },
  addDeviceAndApprove: { ar: 'أضف الجهاز واقبل', en: 'Register and approve' },
  editBeforeAdding: { ar: 'عدّل قبل الإضافة', en: 'Edit before registering' },
  fillVehicleDetails: {
    ar: 'اكتب بيانات العربية عشان تتسجّل',
    en: 'Fill in the vehicle details to register it',
  },
  needVehicleName: {
    ar: 'محتاج اسم للعربية قبل ما تتسجّل',
    en: 'The vehicle needs a name before it can be registered',
  },
  notRegisteredYet: {
    ar: 'الجهاز ده مش مسجّل عندك لسه. الموافقة هتسجّله بالبيانات دي.',
    en: 'This device is not registered yet. Approving registers it with these details.',
  },
  claimNote: { ar: 'ملاحظة للإدارة (اختياري)', en: 'Note for the admin (optional)' },
  claimNotePlaceholder: {
    ar: 'رقم اللوحة، أو مكان الشراء',
    en: 'Plate number, or where you bought it',
  },
  sendRequest: { ar: 'إرسال الطلب', en: 'Send request' },
  requestSent: {
    ar: 'تم إرسال الطلب — في انتظار موافقة الإدارة',
    en: 'Request sent — waiting for approval',
  },
  awaitingApproval: { ar: 'في انتظار الموافقة', en: 'Awaiting approval' },
  requestRejected: { ar: 'الطلب مرفوض', en: 'Request rejected' },
  vehicleAdded: { ar: 'تمت إضافة العربية', en: 'Vehicle added' },

  // admin: the review queue
  claimRequests: { ar: 'طلبات الربط', en: 'Claim requests' },
  requests: { ar: 'الطلبات', en: 'Requests' },
  noRequests: { ar: 'لا توجد طلبات', en: 'No requests' },
  statusPending: { ar: 'في الانتظار', en: 'Pending' },
  statusApproved: { ar: 'مقبول', en: 'Approved' },
  statusRejected: { ar: 'مرفوض', en: 'Rejected' },
  approve: { ar: 'موافقة', en: 'Approve' },
  reject: { ar: 'رفض', en: 'Reject' },
  rejectReason: { ar: 'سبب الرفض (اختياري)', en: 'Reason (optional)' },
  rejectReasonPlaceholder: {
    ar: 'الجهاز مش بتاعك',
    en: 'This device is not yours',
  },
  rejectReasonHint: {
    ar: 'العميل هيشوف السبب ده في حسابه.',
    en: 'The customer sees this reason on their account.',
  },
  imeiNotRegistered: { ar: 'الـ IMEI غير مسجّل عندك', en: 'IMEI is not registered' },
  warnUnknownImei: {
    ar: 'الرقم ده مش موجود في أجهزتك — سجّل الجهاز الأول أو ارفض الطلب.',
    en: 'This IMEI is not one of your devices. Register it first, or reject the request.',
  },
  warnTransfer: {
    ar: 'العربية دي مرتبطة بحساب تاني. الموافقة هتسحبها منه وتديها لصاحب الطلب.',
    en: 'This vehicle belongs to another account. Approving takes it from them and gives it to this requester.',
  },
  confirmTransfer: {
    ar: 'نقل العربية من مالكها الحالي؟',
    en: 'Transfer the vehicle away from its current owner?',
  },
  claimApproved: { ar: 'تمت الموافقة', en: 'Approved' },
  claimRejected: { ar: 'تم الرفض', en: 'Rejected' },
  noVehiclesYet: {
    ar: 'لا توجد عربيات في حسابك بعد',
    en: 'No vehicles in your account yet',
  },
  noVehiclesHelp: {
    ar: 'أضف عربيتك باستخدام كود التفعيل المرفق مع الجهاز.',
    en: 'Add your vehicle using the activation code that came with the tracker.',
  },

  // admin: ownership
  owner: { ar: 'المالك', en: 'Owner' },
  unclaimed: { ar: 'لم يُربط بعد', en: 'Unclaimed' },
  selfRegistered: { ar: 'سجّل بنفسه', en: 'Self-registered' },
  releaseDevice: { ar: 'فك الارتباط', en: 'Release' },
  confirmRelease: {
    ar: 'سيتم فصل الجهاز عن مالكه الحالي وإصدار كود جديد. متأكد؟',
    en: 'This detaches the device from its current owner and issues a new code. Sure?',
  },
  codeCopied: { ar: 'تم نسخ الكود', en: 'Code copied' },
  codeReissued: { ar: 'تم إصدار كود جديد', en: 'New activation code issued' },
  deviceReleased: { ar: 'تم فك الارتباط', en: 'Device released' },
  editDevice: { ar: 'تعديل الجهاز', en: 'Edit device' },
  deviceUpdated: { ar: 'تم حفظ بيانات الجهاز', en: 'Device saved' },
  deviceActive: { ar: 'الجهاز مفعّل', en: 'Device active' },
  imeiCannotChange: {
    ar: 'الـ IMEI مش بيتغيّر — هو اللي الجهاز بيعرّف نفسه بيه. لو غلط، امسح الجهاز وسجّله من جديد.',
    en: 'The IMEI cannot be changed: it is how the device identifies itself. If it is wrong, delete the device and register it again.',
  },
  capture: { ar: 'التقاط الفريمات', en: 'Capture frames' },
  capturing: { ar: 'جارٍ الالتقاط', en: 'Capturing' },
  captureStarted: {
    ar: 'الالتقاط شغال ١٠ دقايق — سيب الجهاز يبعت',
    en: 'Capturing for 10 minutes — let the device report',
  },
  captureStopped: { ar: 'تم إيقاف الالتقاط', en: 'Capture stopped' },
  captureHint: {
    ar: 'بيسجّل كل فريم من الجهاز عشان نتأكد إن البروتوكول مظبوط مع الموديل ده.',
    en: 'Records every frame from this device so the protocol can be verified against this model.',
  },
  copy: { ar: 'نسخ', en: 'Copy' },

  // admin: editing a customer
  edit: { ar: 'تعديل', en: 'Edit' },
  editUser: { ar: 'تعديل بيانات المستخدم', en: 'Edit user' },
  newPassword: { ar: 'كلمة مرور جديدة', en: 'New password' },
  newPasswordHint: {
    ar: 'سيبها فاضية لو مش عايز تغيّرها. لما عميل ينسى كلمة المرور، تكتبها له هنا.',
    en: 'Leave empty to keep the current one. This is where you reset a password for a customer who forgot theirs.',
  },
  accountActive: { ar: 'الحساب مفعّل', en: 'Account active' },
  accountDisabled: { ar: 'موقوف', en: 'Disabled' },
  userUpdated: { ar: 'تم حفظ البيانات', en: 'Saved' },
  passwordReset: { ar: 'تم تغيير كلمة المرور', en: 'Password changed' },
  userDeleted: { ar: 'تم حذف الحساب', en: 'Account deleted' },
  confirmDeleteUser: {
    ar: 'سيتم حذف الحساب نهائياً. عربياته هترجع غير مرتبطة وتقدر تسلّمها لحد تاني.',
    en: 'This deletes the account for good. Its vehicles return to unclaimed and can be handed to someone else.',
  },
  roleAdmin: { ar: 'مدير', en: 'Administrator' },
  roleUser: { ar: 'عميل', en: 'Customer' },
  generate: { ar: 'ولّد واحدة', en: 'Generate' },
  credentialsReady: { ar: 'بيانات الدخول — انسخها للعميل', en: 'Sign-in details — copy these to the customer' },
  copied: { ar: 'تم النسخ', en: 'Copied' },
  copyAll: { ar: 'نسخ الاتنين', en: 'Copy both' },
  passwordShownOnce: {
    ar: 'دي آخر مرة تشوف فيها الباسورد. بعد ما تقفل، مش هينفع يتعرض تاني لأنه متخزّن مشفّر — بس تقدر تحط واحد جديد في أي وقت.',
    en: 'This is the only time this password is visible. It is stored hashed and cannot be shown again — but you can always set a new one.',
  },
  giveCodeToCustomer: {
    ar: 'سلّم هذا الكود للعميل مع الجهاز — بدونه لن يستطيع ربط العربية بحسابه.',
    en: 'Hand this code to the customer with the tracker. Without it they cannot add the vehicle.',
  },

  // generic
  loading: { ar: 'جارٍ التحميل...', en: 'Loading...' },
  error: { ar: 'حدث خطأ', en: 'Something went wrong' },
  retry: { ar: 'إعادة المحاولة', en: 'Retry' },
  close: { ar: 'إغلاق', en: 'Close' },
} as const;

export type StringKey = keyof typeof STRINGS;

export const LangContext = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({
  lang: 'ar',
  setLang: () => {},
});

export function useT() {
  const { lang, setLang } = useContext(LangContext);
  const t = (key: StringKey) => STRINGS[key][lang];
  return { t, lang, setLang, rtl: lang === 'ar' };
}

/** Format a duration in seconds as "2h 14m" / "٢س ١٤د". */
export function formatDuration(seconds: number, lang: Lang): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  const h = lang === 'ar' ? 'س' : 'h';
  const m = lang === 'ar' ? 'د' : 'm';
  return hours > 0 ? `${hours}${h} ${minutes}${m}` : `${minutes}${m}`;
}

export function formatTime(iso: string, lang: Lang): string {
  return new Date(iso).toLocaleTimeString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateTime(iso: string, lang: Lang): string {
  return new Date(iso).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "3 minutes ago" style relative label for the last-seen line. */
export function formatAgo(iso: string | null, lang: Lang): string {
  if (!iso) return lang === 'ar' ? 'غير معروف' : 'unknown';
  const seconds = Math.round((Date.now() - Date.parse(iso)) / 1000);
  if (seconds < 60) return lang === 'ar' ? 'الآن' : 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return lang === 'ar' ? `منذ ${minutes} دقيقة` : `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return lang === 'ar' ? `منذ ${hours} ساعة` : `${hours}h ago`;

  const days = Math.round(hours / 24);
  return lang === 'ar' ? `منذ ${days} يوم` : `${days}d ago`;
}
